import type { ReactElement } from 'react';

import styled from 'styled-components';

import { useProductListContext } from '../../../utilities/context/static/ProductListContext';
import { Title as PLPTitle } from '../../productList/title/Title';
import { CategoryTag } from '../categoryTag/CategoryTag';
import type { Button } from '~/amplienceTypes/schemas/exported/button-schema';
import { media } from '~/components/shared/core/styles';
import {
  BaseStyledModifier,
  CategoryTagScrolling,
  ColorScheme,
} from '~/components/unmanaged/styles/BaseStyledModifiers';
import { BoxSizingBorder } from '~/components/unmanaged/styles/BaseStyledStyling';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  PLPTitle: styled(PLPTitle)`
    ${BaseStyledModifier.Promo}

    .lockup-container {
      font-size: 200%;

      @media ${({ theme }) => media(theme).greaterThan('sm')} {
        font-size: 150%;
      }

      small {
        font-size: clamp(11px, 20%, 13px);
      }
    }
  `,

  Tags: styled.div`
    ${BoxSizingBorder}
    --tags-button-gap: var(--ds-spacing-3xs, 4px);
    --tags-inline-padding: var(--ds-spacing-2xs, 8px);

    display: flex;
    flex-wrap: wrap;
    margin-block-start: var(--ds-spacing-sm, 16px);
    padding: var(--tags-body-padding, 0 var(--tags-inline-padding));
    gap: var(--tags-button-gap);
    justify-content: flex-start;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      --tags-body-padding: 0 var(--ds-spacing-sm, 16px);
    }

    @media ${({ theme }) => media(theme).greaterThan('desktop-sm')} {
      --tags-body-padding: 0 var(--ds-spacing-md, 24px);
    }

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      --categoryTagScrolling-before: 0;
      --categoryTagScrolling-after: 1;
      --categoryTagScrolling-inline-padding: var(--tags-inline-padding);
      --categoryTagScrolling-gradient-padding: var(--tags-inline-padding);

      ${CategoryTagScrolling}
    }

    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        [SiteType.Outlet]: ColorScheme.light,
      })}
  `,
};

export const Title = (): ReactElement => {
  const { categoryDetails, pageContent } = useProductListContext();
  const ampliencePLPTitle = pageContent?.categoryTitle && (
    <div
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: pageContent.categoryTitle as string }}
      suppressHydrationWarning
    />
  );

  return (
    <>
      <S.PLPTitle dataTestId="page-title">{ampliencePLPTitle || categoryDetails?.h1}</S.PLPTitle>
      {pageContent?.categoryTags && (
        <S.Tags>
          {pageContent.categoryTags.map(({ button }: Button, idx) => (
            <CategoryTag {...button} key={`${button?.url}-${idx}`} />
          ))}
        </S.Tags>
      )}
    </>
  );
};
